/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import {
    CacheType,
    generateImageUrlForOmniChanel,
    IAction,
    IActionContext,
    IActionInput,
    IRequestContext,
    versionGte
} from '@msdyn365-commerce/core-internal';
import { getMediaLocationsAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';
// @ts-ignore
import { MediaLocation, MediaType } from '@msdyn365-commerce/retail-proxy';
import { createObservableDataAction } from '../create-data-action.vnext';

/**
 * The action input class for the getMediaLocationsForSelectedVariant data action.
 */
export class MediaLocationsInput implements IActionInput {
    public productId: number;

    public channelId: number;

    public catalogId: number;

    private readonly locale?: string;

    constructor(context: IRequestContext, productId: number, channelId: number, catalogId?: number) {
        this.productId = productId;
        this.channelId = channelId;
        this.catalogId = catalogId || 0;
        this.locale = context.locale;
    }

    public getCacheKey = () => `${this.productId}-${this.channelId}-${this.catalogId}-${this.locale}}`;

    public getCacheObjectType = () => 'ProductMediaLocations';

    public dataCacheType = (): CacheType => 'application';
}

/**
 * The action method for the getMediaLocationsForSelectedVariant data action.
 * @param input
 * @param ctx
 */
export async function getMediaLocationsForProductAction(input: MediaLocationsInput, ctx: IActionContext): Promise<MediaLocation[]> {
    const rsVersion =
        !process.env.MSDyn365Commerce_RSVERSION || process.env.MSDyn365Commerce_RSVERSION === '--'
            ? '0.0'
            : process.env.MSDyn365Commerce_RSVERSION;
    const OmniChannelMediaFeatureVersion = versionGte(rsVersion, '9.43');
    if (!OmniChannelMediaFeatureVersion) {
        return [];
    }
    // eslint-disable-next-line @typescript-eslint/no-require-imports, @typescript-eslint/no-var-requires -- Need for Omni feature.,
    const types = require('@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g');

    // TODO: once the dynamicProvider api for fetching the media locations from the azureCms is implemented
    // we should uncomment these code so we can preview the product in edit mode without publish the product.
    // if (ctx.requestContext.params && ctx.requestContext.params.isEditor) {
    //     return getOmniChannelMediaInfoAsync(ctx.requestContext, input.productId);
    // }

    let productMediaLocations = [];
    try {
        productMediaLocations = await getMediaLocationsAsync(
            {
                callerContext: ctx,
                queryResultSettings: {
                    Paging: { Top: Number.parseInt(ctx.requestContext?.app?.platform?.defaultPageSizeForAPI ?? 100, 10) }
                }
            },
            input.productId,
            input.channelId,
            input.catalogId
        );
    } catch (error) {
        ctx.telemetry.error(`Error running getMediaLocationsAsync action: ${error}`);
        return [];
    }

    // Filtering product images and videos only
    let productImagesAndVideos = productMediaLocations.filter(
        // @ts-ignore
        media => media.MediaTypeValue === types.MediaType.Image || media.MediaTypeValue === types.MediaType.Video
    );
    if (productImagesAndVideos.length === 0) {
        return [];
    }

    // sorting the images by display order settings.
    productImagesAndVideos = productImagesAndVideos.sort((a, b) => {
        // @ts-ignore
        if (typeof a.DisplayOrder === 'undefined' && typeof b.DisplayOrder === 'undefined') {
            return 0;
            // @ts-ignore
        } else if (typeof a.DisplayOrder === 'undefined') {
            return 1;
            // @ts-ignore
        } else if (typeof b.DisplayOrder === 'undefined') {
            return -1;
        } else {
            // @ts-ignore
            return a.DisplayOrder - b.DisplayOrder;
        }
    });
    const productMedia = productImagesAndVideos.map((mediaLocation: MediaLocation) => {
        const mediaLoc = { ...mediaLocation };
        // @ts-ignore
        if (mediaLoc.MediaTypeValue === types.MediaType.Image) {
            mediaLoc.Uri = generateImageUrlForOmniChanel(ctx.requestContext.apiSettings, mediaLocation.Uri);
        }
        return mediaLoc;
    });

    return productMedia;
}

/**
 * The getMediaLocationsForSelectedVariant data action
 * Gets the currently selected variant for the page via the getSelectedVariant data action,
 * and then gets the media location information for the variant via the MediaLocations RetailServer API.
 */
export const getMediaLocationsForSelectedVariantActionDataAction = createObservableDataAction({
    id: '@msdyn365-commerce/get-mediaLocations',
    action: <IAction<MediaLocation[] | null>>getMediaLocationsForProductAction
});

export default getMediaLocationsForProductAction;
